import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectProps,
} from '@mui/material';

/**
 * Select field component that integrates with React Hook Form
 * Uses MUI Select and handles validation, errors, etc.
 */
interface FormSelectFieldProps extends Omit<SelectProps, 'name'> {
  name: string;
  label: string;
  helperText?: string;
  options: Array<{ value: string | number; label: string }>;
}

export const FormSelectField: React.FC<FormSelectFieldProps> = ({
  name,
  label,
  helperText,
  options,
  required,
  ...other
}) => {
  const { control, formState: { errors } } = useFormContext();
  const errorMessage = errors[name]?.message as string | undefined;
  const labelId = `${name}-label`;
  
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <FormControl
          fullWidth
          error={!!errorMessage}
          required={required}
        >
          <InputLabel id={labelId}>{label}</InputLabel>
          <Select
            {...field}
            labelId={labelId}
            label={label}
            value={field.value ?? ''}
            {...other}
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {(errorMessage || helperText) && (
            <FormHelperText>{errorMessage || helperText}</FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
};

export default FormSelectField;